import { useState, useEffect } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Activity,
  Award,
  BookOpen,
  BrainCircuit,
  Bot,
  CalendarDays,
  ChevronDown,
  Crown,
  Dices,
  Globe,
  GraduationCap,
  LayoutDashboard,
  LineChart,
  LogOut,
  Medal,
  Menu,
  Network,
  Newspaper,
  LifeBuoy,
  UserX,
  Radio,
  Send,
  Server,
  Settings,
  Trophy,
  User,
  Users,
  Wallet,
  Coins,
  X,
  MessageSquareText,
  RadioTower,
} from "lucide-react";
import Logo from "@/components/Logo";
import NotificationBell from "@/components/NotificationBell";
import CoinBalanceChip from "@/components/CoinBalanceChip";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const NAV_CONFIG = {
  member: [
    { group: "Main" },
    { to: "/member", label: "Overview", icon: LayoutDashboard, end: true },
    { to: "/member/news", label: "News", icon: Newspaper },
    { to: "/member/feedback", label: "Feedback", icon: MessageSquareText },
    { to: "/member/support", label: "Plusmaker Support", icon: LifeBuoy },
    { group: "Trading" },
    { to: "/member/signal-providers", label: "Signal Provider", icon: RadioTower },
    { to: "/member/indicators", label: "Indicators", icon: LineChart },
    { to: "/member/expert-advisors", label: "Expert Advisor", icon: Bot },
    { to: "/member/vps", label: "VPS", icon: Server, soon: true },
    { group: "Rewards & Achievements" },
    { to: "/member/rewards", label: "Reward Mall", icon: Trophy },
    { to: "/member/lucky-draw", label: "Lucky Draw", icon: Dices },
    { to: "/member/top-members", label: "Top 10", icon: Crown },
    { to: "/member/certificates", label: "My Certificates", icon: Award },
    { group: "Account" },
    { to: "/member/coins", label: "Coins", icon: Coins },
    { to: "/member/profile", label: "Profile", icon: User },
    { group: "PLUSMAKER LAB" },
    { to: "/member/topics", label: "Topics", icon: Send },
    { to: "/member/live-learn", label: "Live & Learn", icon: Radio },
    { to: "/member/ai", label: "PlusMaker AI", icon: BrainCircuit, soon: true },
    { to: "/member/trading-journal", label: "Your Trading Journal", icon: BookOpen, soon: true },
    { to: "/member/education", label: "Education", icon: GraduationCap, soon: true },
  ],
  ib: [
    { to: "/ib", label: "Overview", icon: LayoutDashboard, end: true },
    { to: "/ib/clients", label: "My Clients", icon: Users },
    { to: "/ib/indicator-requests", label: "Indicator Requests", icon: LineChart },
    { to: "/ib/expert-advisor-requests", label: "Expert Advisor Requests", icon: Bot },
    { to: "/ib/topics", label: "Topics", icon: Send },
    { to: "/ib/activity", label: "My Activity", icon: Activity },
    { to: "/ib/news", label: "News", icon: Newspaper },
    { to: "/ib/wallet", label: "IB Wallet", icon: Wallet },
  ],
  admin: [
    { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
    { to: "/admin/members", label: "Members", icon: Users },
    { to: "/admin/deletion-requests", label: "Deletion Requests", icon: UserX },
    { to: "/admin/ranks", label: "Rank Management", icon: Medal },
    { to: "/admin/ibs", label: "IB Management", icon: Network },
    { to: "/admin/rewards", label: "Reward Management", icon: Trophy },
    { to: "/admin/indicators", label: "Indicator Management", icon: LineChart },
    { to: "/admin/news", label: "News", icon: Newspaper },
    { to: "/admin/feedback", label: "Feedback", icon: MessageSquareText },
    { to: "/admin/expert-advisors", label: "Expert Advisors", icon: Bot },
    { to: "/admin/wallet", label: "Wallet & Withdrawals", icon: Wallet },
    { to: "/admin/coins", label: "Coins", icon: Wallet },
    { to: "/admin/coin-orders", label: "Coin Top-Ups", icon: Coins },
    { to: "/admin/local-depositors", label: "Local Depositors", icon: Wallet },
    { to: "/admin/certificates", label: "Certificates", icon: Award },
    { to: "/admin/lucky-draw", label: "Lucky Draw", icon: Dices },
    { to: "/admin/ai", label: "PlusMaker AI", icon: BrainCircuit },
    { to: "/admin/content", label: "Website Content", icon: Globe },
    { to: "/admin/settings", label: "Settings", icon: Settings },
  ],
};

const ROLE_LABELS = {
  member: "Member",
  ib: "Introducing Broker",
  admin: "Administrator",
};

// Mobile-only bottom navigation. Every `to`/icon is an existing authorised route from
// NAV_CONFIG/router (no new routes). Member shows 5 destinations and NO Menu; IB/Admin show
// 4 destinations + a Menu tab that opens the existing hamburger drawer (full navigation).
const BOTTOM_NAV = {
  member: [
    { to: "/member", label: "Home", icon: LayoutDashboard, end: true },
    { to: "/member/topics", label: "Topics", icon: Send },
    { to: "/member/rewards", label: "Rewards", icon: Trophy },
    { to: "/member/lucky-draw", label: "Lucky Draw", icon: Dices },
    { to: "/member/profile", label: "Profile", icon: User },
  ],
  ib: [
    { to: "/ib", label: "Home", icon: LayoutDashboard, end: true },
    { to: "/ib/clients", label: "Clients", icon: Users },
    { to: "/ib/topics", label: "Topics", icon: Send },
    { to: "/ib/wallet", label: "Wallet", icon: Wallet },
  ],
  admin: [
    { to: "/admin", label: "Home", icon: LayoutDashboard, end: true },
    { to: "/admin/ibs", label: "IB Mgmt", icon: Network },
    { to: "/admin/signal-providers", label: "Signal Providers", icon: RadioTower },
    { to: "/admin/indicators", label: "Indicators", icon: LineChart },
    { to: "/admin/rewards", label: "Redemption", icon: Trophy },
  ],
};

function MobileBottomNav({ role, onOpenMenu, topicsBadge = 0 }) {
  const items = BOTTOM_NAV[role] || [];
  const showMenu = role !== "member"; // member gets 5 direct tabs, no Menu
  return (
    <nav
      className="lg:hidden fixed bottom-0 inset-x-0 z-40 glass-nav border-t bottom-nav-safe"
      data-testid={`mobile-bottom-nav-${role}`}
      aria-label="Primary"
    >
      <div className="flex items-stretch justify-around">
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            data-testid={`bottom-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `relative flex flex-col items-center justify-center gap-1 flex-1 min-w-0 min-h-[56px] py-1.5 px-0.5 text-[10px] font-medium transition-colors ${
                isActive ? "text-[#D4AF37]" : "text-[#94A3B8]"
              }`
            }
          >
            {item.label === "Topics" && topicsBadge > 0 && (
              <span
                data-testid="bottom-nav-topics-badge"
                className="absolute top-1 left-1/2 ml-1 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold leading-none flex items-center justify-center ring-2 ring-[#0B0B0F]"
              >
                {topicsBadge > 99 ? "99+" : topicsBadge}
              </span>
            )}
            <item.icon className="h-5 w-5 shrink-0" />
            <span className="leading-tight text-center w-full truncate">{item.label}</span>
          </NavLink>
        ))}
        {showMenu && (
          <button
            type="button"
            onClick={onOpenMenu}
            data-testid="bottom-nav-menu"
            aria-label="Open full menu"
            className="flex flex-col items-center justify-center gap-1 flex-1 min-w-0 min-h-[56px] py-1.5 px-0.5 text-[10px] font-medium text-[#94A3B8] transition-colors hover:text-white"
          >
            <Menu className="h-5 w-5 shrink-0" />
            <span className="leading-tight text-center w-full truncate">Menu</span>
          </button>
        )}
      </div>
    </nav>
  );
}

// Admin navigation is grouped into categories. Desktop shows static category
// headings; mobile shows compact, expandable sections (the category holding the
// current page opens automatically). Every route below is an existing admin link
// — none removed, renamed, or re-routed.
const ADMIN_OVERVIEW = { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true };
const ADMIN_SUPPORT = { to: "/admin/support", label: "Plusmaker Support", icon: LifeBuoy };
const ADMIN_GROUPS = [
  { category: "People", items: [
    { to: "/admin/members", label: "Members", icon: Users },
    { to: "/admin/ibs", label: "IB Management", icon: Network },
    { to: "/admin/ranks", label: "Rank Management", icon: Medal },
    { to: "/admin/deletion-requests", label: "Deletion Requests", icon: UserX },
  ] },
  { category: "Trading", items: [
    { to: "/admin/signal-providers", label: "Signal Providers", icon: RadioTower },
    { to: "/admin/indicators", label: "Indicator Management", icon: LineChart },
    { to: "/admin/expert-advisors", label: "Expert Advisors", icon: Bot },
  ] },
  { category: "Rewards", items: [
    { to: "/admin/rewards", label: "Reward Management", icon: Trophy },
    { to: "/admin/lucky-draw", label: "Lucky Draw", icon: Dices },
    { to: "/admin/certificates", label: "Certificates", icon: Award },
  ] },
  { category: "Finance", items: [
    { to: "/admin/wallet", label: "Wallet & Withdrawals", icon: Wallet },
    { to: "/admin/coins", label: "Coins", icon: Wallet },
    { to: "/admin/coin-orders", label: "Coin Top-Ups", icon: Coins },
    { to: "/admin/local-depositors", label: "Local Depositors", icon: Wallet },
  ] },
  { category: "Content & System", items: [
    { to: "/admin/news", label: "News", icon: Newspaper },
    { to: "/admin/feedback", label: "Feedback", icon: MessageSquareText },
    { to: "/admin/content", label: "Website Content", icon: Globe },
    { to: "/admin/ai", label: "PlusMaker AI", icon: BrainCircuit },
    { to: "/admin/settings", label: "Settings", icon: Settings },
  ] },
];

const navLinkClass = ({ isActive }) =>
  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors duration-200 ${
    isActive
      ? "bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/25"
      : "text-[#94A3B8] hover:text-white hover:bg-[#121318] border border-transparent"
  }`;

function AdminNav({ collapsible, onNavigate }) {
  const { pathname } = useLocation();
  const activeCat =
    ADMIN_GROUPS.find((g) =>
      g.items.some((it) => pathname === it.to || pathname.startsWith(`${it.to}/`)),
    )?.category || null;
  const [open, setOpen] = useState(() =>
    Object.fromEntries(ADMIN_GROUPS.map((g) => [g.category, g.category === activeCat])),
  );
  useEffect(() => {
    if (activeCat) setOpen((o) => ({ ...o, [activeCat]: true }));
  }, [activeCat]);

  const renderLink = (item) => (
    <NavLink
      key={item.to}
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      data-testid={`sidebar-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
      className={navLinkClass}
    >
      <item.icon className="h-4 w-4 shrink-0" />
      <span className="flex-1">{item.label}</span>
    </NavLink>
  );

  return (
    <>
      {renderLink(ADMIN_OVERVIEW)}
      {renderLink(ADMIN_SUPPORT)}
      {ADMIN_GROUPS.map((g) => {
        const slug = g.category.toLowerCase().replace(/[\s&]+/g, "-");
        const expanded = collapsible ? !!open[g.category] : true;
        return (
          <div key={g.category} className="pt-1.5">
            {collapsible ? (
              <button
                type="button"
                onClick={() => setOpen((o) => ({ ...o, [g.category]: !o[g.category] }))}
                aria-expanded={expanded}
                data-testid={`sidebar-cat-${slug}`}
                className="w-full flex items-center justify-between px-3 pt-3 pb-1.5 text-[9px] font-mono tracking-[0.28em] uppercase text-[#64748B]/70 hover:text-[#94A3B8] transition-colors"
              >
                <span>{g.category}</span>
                <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`} />
              </button>
            ) : (
              <p
                className="px-3 pt-4 pb-1.5 text-[9px] font-mono tracking-[0.28em] uppercase text-[#64748B]/70"
                data-testid={`sidebar-cat-${slug}`}
              >
                {g.category}
              </p>
            )}
            {expanded && <div className="space-y-1">{g.items.map(renderLink)}</div>}
          </div>
        );
      })}
    </>
  );
}

function SidebarContent({ role, onNavigate, variant, topicsUnread = 0 }) {
  const base = NAV_CONFIG[role] || [];
  const [items, setItems] = useState(base);
  useEffect(() => {
    if (role !== "ib") { setItems(NAV_CONFIG[role] || []); return; }
    api.get("/ib/local-depositor/status")
      .then((res) => {
        const b = NAV_CONFIG.ib || [];
        setItems(res.data?.is_active_depositor
          ? [...b, { to: "/ib/local-depositor", label: "Local Depositor", icon: Wallet }]
          : b);
      })
      .catch(() => setItems(NAV_CONFIG.ib || []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);
  return (
    <div className="flex flex-col h-full">
      <div className="px-5 pb-5 border-b border-[#222530] drawer-safe-top">
        <Logo />
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto" data-testid={`sidebar-nav-${role}`}>
        {role === "admin" ? (
          <AdminNav collapsible={variant === "mobile"} onNavigate={onNavigate} />
        ) : (
          items.map((item, idx) =>
          item.group ? (
            <p
              key={`group-${idx}`}
              className="px-3 pt-5 pb-1.5 text-[9px] font-mono tracking-[0.28em] uppercase text-[#64748B]/70 first:pt-1"
              data-testid={`sidebar-group-${item.group.toLowerCase().replace(/[\s&]+/g, "-")}`}
            >
              {item.group}
            </p>
          ) : (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onNavigate}
            data-testid={`sidebar-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors duration-200 ${
                isActive
                  ? "bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/25"
                  : "text-[#94A3B8] hover:text-white hover:bg-[#121318] border border-transparent"
              }`
            }
          >
            <item.icon className="h-4 w-4 shrink-0" />
            <span className="flex-1">{item.label}</span>
            {item.label === "Topics" && topicsUnread > 0 && (
              <span
                data-testid="sidebar-topics-badge"
                className="min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none flex items-center justify-center"
              >
                {topicsUnread > 99 ? "99+" : topicsUnread}
              </span>
            )}
            {item.soon && (
              <span className="text-[9px] font-mono tracking-widest uppercase text-[#D4AF37]/80 border border-[#D4AF37]/30 rounded-full px-1.5 py-0.5">
                Soon
              </span>
            )}
          </NavLink>
          )
        )
        )}
      </nav>
      <div className="px-5 py-4 border-t border-[#222530]">
        <p className="text-[10px] font-mono tracking-[0.25em] uppercase text-[#64748B]">
          PLUSMAKER Ecosystem
        </p>
      </div>
    </div>
  );
}

export default function DashboardLayout({ role }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [topicsUnread, setTopicsUnread] = useState(0);

  // Topics unread badge (member only): reuses the existing signals read-state via a
  // sponsor-isolated count endpoint. Polls 30s (paused when hidden) and refreshes
  // immediately when the broadcast is read (pm-topics-unread-refresh event).
  useEffect(() => {
    if (role !== "member") return undefined;
    let timer = null;
    const load = () => api.get("/member/activity/signals/unread-count")
      .then((r) => setTopicsUnread(r.data?.count || 0)).catch(() => {});
    const start = () => { if (!timer) timer = setInterval(load, 30000); };
    const stop = () => { if (timer) { clearInterval(timer); timer = null; } };
    const onVis = () => { if (document.hidden) stop(); else { load(); start(); } };
    const onRefresh = () => load();
    load();
    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pm-topics-unread-refresh", onRefresh);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pm-topics-unread-refresh", onRefresh);
    };
  }, [role]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="app-shell bg-[#050506]" data-testid={`${role}-dashboard-layout`}>
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col glass-nav border-r z-40">
        <SidebarContent role={role} variant="desktop" topicsUnread={role === "member" ? topicsUnread : 0} />
      </aside>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50" data-testid="mobile-sidebar-overlay">
          <div className="absolute inset-0 bg-black/70" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 glass-nav border-r">
            <button
              className="absolute right-4 text-[#94A3B8] hover:text-white p-1 drawer-close-safe"
              onClick={() => setMobileOpen(false)}
              data-testid="mobile-sidebar-close"
              aria-label="Close menu"
            >
              <X className="h-5 w-5" />
            </button>
            <SidebarContent role={role} onNavigate={() => setMobileOpen(false)} variant="mobile" topicsUnread={role === "member" ? topicsUnread : 0} />
          </aside>
        </div>
      )}

      {/* Main column */}
      <div className="lg:pl-64 flex flex-col app-shell">
        <header className="sticky top-0 z-30 glass-nav border-b safe-top">
          <div className="flex items-center justify-between px-4 sm:px-6 lg:px-8 h-16">
            <div className="flex items-center gap-3">
              <button
                className="lg:hidden text-white inline-flex items-center justify-center min-h-[44px] min-w-[44px] -ml-2"
                onClick={() => setMobileOpen(true)}
                data-testid="dashboard-menu-toggle"
                aria-label="Open menu"
              >
                <Menu className="h-6 w-6" />
              </button>
              <span className="eyebrow-label hidden sm:inline">{ROLE_LABELS[role]} Portal</span>
            </div>
            <div className="flex items-center gap-4">
              {role === "member" && <CoinBalanceChip />}
              <NotificationBell />
              <div className="text-right hidden sm:block">
                <p className="text-sm font-semibold text-white leading-tight" data-testid="dashboard-user-name">
                  {user?.full_name}
                </p>
                <p className="text-xs text-[#64748B] font-mono" data-testid="dashboard-user-username">
                  {user?.username}
                </p>
              </div>
              <span className="text-[10px] font-mono tracking-widest uppercase text-emerald-400 bg-emerald-400/10 border border-emerald-400/30 rounded-full px-2.5 py-1" data-testid="dashboard-role-badge">
                {role}
              </span>
              <button
                onClick={handleLogout}
                data-testid="logout-button"
                className="flex items-center gap-2 text-sm text-[#94A3B8] hover:text-red-400 transition-colors"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </header>

        <main className="flex-1 px-4 sm:px-6 lg:px-8 pt-8 pb-[calc(4.5rem+env(safe-area-inset-bottom,0px))] lg:pb-8">
          <Outlet />
        </main>
      </div>

      <MobileBottomNav role={role} onOpenMenu={() => setMobileOpen(true)} topicsBadge={role === "member" ? topicsUnread : 0} />
    </div>
  );
}
